import { useEffect, useRef, useCallback } from 'react';
import { SVGComposer } from 'svg-composer';
import { useEditorStore } from '@/stores/editorStore';

interface UseSVGComposerOptions {
  width: number;
  height: number;
  onContentChange?: () => void;
}

export function useSVGComposer(
  containerRef: React.RefObject<HTMLDivElement>,
  options: UseSVGComposerOptions
) {
  const composerRef = useRef<SVGComposer | null>(null);
  const { selectElements, clearSelection } = useEditorStore();

  // Initialize composer
  useEffect(() => {
    if (!containerRef.current || composerRef.current) return;

    const composer = new SVGComposer(containerRef.current, {
      width: options.width,
      height: options.height,
    });

    // Sync selection changes to store
    composer.on('selection:changed', ({ selectedIds }: { selectedIds: string[] }) => {
      selectElements(selectedIds);
    });

    // Track state changes for dirty flag
    composer.on('state:changed', () => {
      options.onContentChange?.();
    });

    composerRef.current = composer;

    return () => {
      composer.destroy();
      composerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef, options.width, options.height]);

  // Load content into composer
  const loadContent = useCallback((json: string) => {
    if (!composerRef.current) return;

    try {
      if (json && json.trim()) {
        composerRef.current.fromJSON(json);
      }
      composerRef.current.render();
    } catch (err) {
      console.error('Failed to load content:', err);
    }
  }, []);

  // Get content from composer
  const getContent = useCallback((): string => {
    if (!composerRef.current) return '';
    return composerRef.current.toJSON();
  }, []);

  // Get SVG export
  const getSVG = useCallback((): string => {
    if (!composerRef.current) return '';
    return composerRef.current.toSVG();
  }, []);

  // Add element
  const addElement = useCallback((element: Parameters<SVGComposer['addElement']>[0]): string | undefined => {
    if (!composerRef.current) return undefined;
    return composerRef.current.addElement(element);
  }, []);

  // Remove selected elements
  const removeSelected = useCallback(() => {
    if (!composerRef.current) return;
    const selected = composerRef.current.getSelection();
    if (selected.length > 0) {
      composerRef.current.removeElements(selected);
    }
  }, []);

  // Clear selection
  const clearSelectionHandler = useCallback(() => {
    if (!composerRef.current) return;
    composerRef.current.clearSelection();
    clearSelection();
  }, [clearSelection]);

  // Undo/Redo
  const undo = useCallback(() => {
    if (!composerRef.current) return;
    composerRef.current.undo();
  }, []);

  const redo = useCallback(() => {
    if (!composerRef.current) return;
    composerRef.current.redo();
  }, []);

  const canUndo = useCallback(() => {
    if (!composerRef.current) return false;
    return composerRef.current.canUndo();
  }, []);

  const canRedo = useCallback(() => {
    if (!composerRef.current) return false;
    return composerRef.current.canRedo();
  }, []);

  // Select all
  const selectAll = useCallback(() => {
    if (!composerRef.current) return;
    composerRef.current.selectAll();
  }, []);

  // Set tool
  const setTool = useCallback((tool: 'select' | 'pan' | 'add-image' | 'add-text' | 'add-shape') => {
    if (!composerRef.current) return;
    composerRef.current.setTool(tool);
  }, []);

  // Get element by ID
  const getElement = useCallback((id: string) => {
    if (!composerRef.current) return undefined;
    return composerRef.current.getElement(id);
  }, []);

  // Update element properties
  const updateElement = useCallback((id: string, updates: Record<string, unknown>) => {
    if (!composerRef.current) return;
    composerRef.current.updateElement(id, updates);
  }, []);

  return {
    composer: composerRef,
    loadContent,
    getContent,
    getSVG,
    addElement,
    removeSelected,
    clearSelection: clearSelectionHandler,
    undo,
    redo,
    canUndo,
    canRedo,
    selectAll,
    setTool,
    getElement,
    updateElement,
  };
}
